import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { day, inr, num } from '../lib/format.js';
import { parseNumber } from '../lib/numbers.js';
import { Alert, Card, CardSkeleton, Empty, LoadMore, Pill, useAsync, usePagedList } from '../components/ui.jsx';
import PeriodPicker, { useUrlPeriod } from '../components/PeriodPicker.jsx';
import ExportButton, { fetchAllPages, periodSlug, periodText } from '../components/ExportButton.jsx';
import { useEntityQuery } from '../lib/entity.js';

const REASONS = {
  sales_return: 'Sales return',
  rate_difference: 'Rate difference',
  post_sale_discount: 'Post-sale discount',
  other: 'Other',
};

export default function CreditNotes() {
  const [params] = useSearchParams();
  const status = params.get('status') || '';
  // Drafts are work still to finish, whenever they were started.
  const entityQuery = useEntityQuery();
  const { period, setPeriod, query } = useUrlPeriod({ allByDefault: status === 'draft' });
  const list = usePagedList(`/api/credit-notes?${[status && `status=${status}`, query, entityQuery].filter(Boolean).join('&')}`, 'credit_notes');
  const navigate = useNavigate();

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Credit notes</h1>
          <div className="page-sub">
            The only correct way to undo or reduce an issued invoice. Returned goods go back to the batch they left
            on, and the note is posted to Tally as its own voucher.
          </div>
        </div>
        <div className="inline">
          <PeriodPicker allowAll value={period} onChange={setPeriod} />
          <ExportButton build={async () => ({
            filename: `credit-notes-${periodSlug(period)}`,
            title: 'Credit notes',
            subtitle: periodText(period),
            sheets: [{
              name: 'Credit notes',
              columns: [
                { key: 'cn_number', header: 'Credit note' }, { key: 'cn_date', header: 'Date', type: 'date' },
                { key: 'invoice_number', header: 'Against invoice' }, { key: 'buyer_name', header: 'Buyer' },
                { key: 'reason_text', header: 'Reason' }, { key: 'returned', header: 'Goods returned' },
                { key: 'taxable_value', header: 'Taxable', type: 'money', total: true },
                { key: 'grand_total', header: 'Total', type: 'money', total: true },
                { key: 'status', header: 'Status' }, { key: 'tally_sync_status', header: 'Tally' },
              ],
              rows: (await fetchAllPages(
                `/api/credit-notes?${[status && `status=${status}`, query, entityQuery].filter(Boolean).join('&')}`,
                'credit_notes')).map((n) => ({
                ...n, reason_text: REASONS[n.reason] || n.reason, returned: n.goods_returned ? 'Yes' : 'No',
              })),
            }],
          })} />
          <Link className="btn secondary" to="/invoices">Pick an invoice to credit</Link>
        </div>
      </div>

      <Alert onRetry={list.reload}>{list.error}</Alert>
      {list.loading && !list.data && <CardSkeleton lines={4} />}

      {list.data && (
        <Card pad={list.data.credit_notes.length === 0}>
          {list.data.credit_notes.length === 0 ? (
            <Empty title="No credit notes"
              action={<Link className="btn" to="/invoices">Open an invoice</Link>}>
              Raise one from the invoice it corrects.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead>
                  <tr><th>Credit note</th><th>Against</th><th>Buyer</th><th>Reason</th>
                    <th className="r">Taxable</th><th className="r">Credited</th><th>Status</th><th>Tally</th></tr>
                </thead>
                <tbody>
                  {list.data.credit_notes.map((c) => (
                    <tr key={c.id} className="clickable" onClick={() => navigate(`/credit-notes/${c.id}`)}>
                      <td className="mono">{c.cn_number || 'draft'}<div className="sub">{day(c.cn_date)}</div></td>
                      <td className="mono">{c.invoice_number}</td>
                      <td>{c.buyer_name}</td>
                      <td>{REASONS[c.reason] || c.reason}
                        <div className="sub">{c.goods_returned ? 'goods returned' : 'value only'}</div></td>
                      <td className="r num">{inr(c.taxable_value, 0)}</td>
                      <td className="r num"><b>{inr(c.grand_total, 0)}</b></td>
                      <td><Pill value={c.status} /></td>
                      <td><Pill value={c.tally_sync_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <LoadMore list={list} />
        </Card>
      )}
    </>
  );
}

/** Raised from the invoice screen: what is left to credit, and why. */
export function CreditNoteForm({ invoiceId, onDone, onCancel }) {
  const source = useAsync(() => api.get(`/api/invoices/${invoiceId}/creditable`), [invoiceId]);
  const [reason, setReason] = useState('sales_return');
  const [narration, setNarration] = useState('');
  const [qtys, setQtys] = useState({});
  const [rates, setRates] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (source.loading && !source.data) return <CardSkeleton lines={3} />;
  if (source.error) return <Alert onRetry={source.reload}>{source.error}</Alert>;

  const lines = source.data.lines;
  const isReturn = reason === 'sales_return';

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      // Lines left blank are not being credited; anything typed that is not a
      // number is an error, never a line silently dropped from the note.
      const out = [];
      for (const l of lines) {
        const q = parseNumber(qtys[l.invoice_line_id]);
        const r = isReturn ? null : parseNumber(rates[l.invoice_line_id]);
        if ((q === null || q === 0) && r === null) continue;
        const where = l.description || 'A line';
        if (!Number.isFinite(q) || q <= 0) throw new Error(`${where}: the quantity must be a number above zero`);
        if (!isReturn && (!Number.isFinite(r) || r <= 0)) throw new Error(`${where}: the reduction per unit must be a number above zero`);
        out.push({ invoice_line_id: l.invoice_line_id, qty: q, ...(isReturn ? {} : { rate: r }) });
      }
      const payload = {
        invoice_id: invoiceId,
        reason,
        narration: narration || undefined,
        issue: true,
        lines: out,
      };
      if (!payload.lines.length) throw new Error('Enter a quantity on at least one line');
      const cn = await api.post('/api/credit-notes', payload);
      onDone ? onDone(cn) : navigate('/credit-notes');
    } catch (e) {
      setError(e.details ? `${e.message} — ${JSON.stringify(e.details)}` : e.message);
    } finally { setBusy(false); }
  };

  return (
    <Card title="Raise a credit note"
      note={`Against ${source.data.invoice.invoice_number} · ${source.data.invoice.buyer_name}`}
      actions={<button className="btn small ghost" onClick={onCancel}>Close</button>}>
      <Alert>{error}</Alert>
      <div className="row">
        <div className="field">
          <label>Reason</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}>
            {Object.entries(REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Narration</label>
          <input value={narration} onChange={(e) => setNarration(e.target.value)}
            placeholder="Buyer rejected two drums on colour" />
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Item</th><th className="r">Billed</th><th className="r">Already credited</th>
              <th className="r">Creditable</th>
              <th className="r" style={{ width: 150 }}>{isReturn ? 'Return quantity' : 'Quantity'}</th>
              {!isReturn && <th className="r" style={{ width: 150 }}>Reduction per unit</th>}</tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.invoice_line_id}>
                <td>{l.description}<div className="sub">billed at {inr(l.rate)} · GST {l.gst_rate}%</div></td>
                <td className="r num">{num(l.qty)}</td>
                <td className="r num">{num(l.qty_credited)}</td>
                <td className="r num"><b>{num(l.qty_creditable)}</b></td>
                <td className="r">
                  <input className="num" style={{ textAlign: 'right' }} inputMode="decimal"
                    value={qtys[l.invoice_line_id] ?? ''}
                    placeholder={isReturn ? String(l.qty_creditable) : String(l.qty)}
                    onChange={(e) => setQtys({ ...qtys, [l.invoice_line_id]: e.target.value })} />
                </td>
                {!isReturn && (
                  <td className="r">
                    <input className="num" style={{ textAlign: 'right' }} inputMode="decimal"
                      value={rates[l.invoice_line_id] ?? ''} placeholder="e.g. 6.00"
                      onChange={(e) => setRates({ ...rates, [l.invoice_line_id]: e.target.value })} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-note" style={{ marginTop: 10 }}>
        {isReturn
          ? 'The returned quantity goes back to the batches this line was despatched on, and becomes available to sell again straight away.'
          : 'A rate difference moves value only — no stock comes back. Enter the reduction per unit, not the new rate.'}
      </div>

      <div className="inline" style={{ marginTop: 14 }}>
        <button className="btn" disabled={busy} onClick={submit}>{busy ? 'Working…' : 'Issue credit note'}</button>
        <button className="btn secondary" onClick={onCancel}>Cancel</button>
      </div>
    </Card>
  );
}
